import React, { useMemo, useState } from "react";
import ReactEcharts from "echarts-for-react";
import { Card, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useProductionFilter } from "./ProductionFilterContext";

const style = {
  height: "350px",
  width: "100%",
};

export default function ProductionVolumeChart({ data, colorMap }) {
  const { t } = useTranslation();
  const { selectedYear, selectedQuarter, setSelectedYear, setSelectedQuarter } =
    useProductionFilter();
  const [networkType, setNetworkType] = useState("All");
  const [legendState, setLegendState] = useState({});

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (networkType === "All") return data;
    return data.filter((sensor) => sensor.network_type === networkType);
  }, [data, networkType]);

  const chartOption = useMemo(() => {
    // 1. Determine X-Axis Categories
    let xAxisCategories = [];
    let groupFn = () => "";

    if (selectedYear === "all") {
      // Group by Year
      const years = new Set(
        filteredData
          .map((s) => new Date(s.produced_at).getFullYear())
          .filter((y) => !isNaN(y) && y >= 2023)
      );

      let minYear = 2023;
      if (years.size > 0) {
        minYear = Math.max(2023, Math.min(...years, new Date().getFullYear() - 2));
      }

      let maxYear = new Date().getFullYear();
      if (years.size > 0) {
        maxYear = Math.max(...years, new Date().getFullYear());
      }

      for (let y = minYear; y <= maxYear; y++) xAxisCategories.push(y.toString());
      groupFn = (date) => date.getFullYear().toString();
    } else if (selectedQuarter === "all") {
      // Group by Quarter
      xAxisCategories = ["Q1", "Q2", "Q3", "Q4"];
      groupFn = (date) => {
        const month = date.getMonth();
        return `Q${Math.floor(month / 3) + 1}`;
      };
    } else {
      // Group by Month
      let startMonth = 0;
      if (selectedQuarter === "Q1") startMonth = 0;
      if (selectedQuarter === "Q2") startMonth = 3;
      if (selectedQuarter === "Q3") startMonth = 6;
      if (selectedQuarter === "Q4") startMonth = 9;

      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      xAxisCategories = [
        monthNames[startMonth],
        monthNames[startMonth + 1],
        monthNames[startMonth + 2],
      ];
      groupFn = (date) => monthNames[date.getMonth()];
    }

    // 2. Identify Hardware Models
    const models = new Set();
    filteredData.forEach((s) => {
      if (s.sensor_type_key) models.add(s.sensor_type_key);
      else models.add("Unknown");
    });
    const modelArray = Array.from(models);

    // 3. Aggregate Data
    // Series data: { [modelName]: [count1, count2, ...] }
    const seriesDataMap = {};
    modelArray.forEach((m) => (seriesDataMap[m] = Array(xAxisCategories.length).fill(0)));

    filteredData.forEach((s) => {
      if (!s.produced_at) return;
      const date = new Date(s.produced_at);
      const xKey = groupFn(date);
      const xIndex = xAxisCategories.indexOf(xKey);
      if (xIndex !== -1) {
        const model = s.sensor_type_key || "Unknown";
        seriesDataMap[model][xIndex]++;
      }
    });

    // Calculate totals for visible series labels
    const totals = Array(xAxisCategories.length).fill(0);
    modelArray.forEach((m) => {
      const isVisible = legendState[m] !== false;
      if (isVisible) {
        seriesDataMap[m].forEach((val, i) => (totals[i] += val));
      }
    });

    const series = modelArray.map((m) => {
      return {
        name: m,
        type: "bar",
        stack: "total",
        data: seriesDataMap[m],
        cursor: selectedQuarter === "all" ? "pointer" : "default",
        itemStyle: {
          ...(colorMap && colorMap[m] ? { color: colorMap[m] } : {}),
        },
      };
    });

    // Add dummy series for total labels at the top
    series.push({
      name: "Total",
      type: "bar",
      stack: "total",
      cursor: selectedQuarter === "all" ? "pointer" : "default",
      label: {
        show: true,
        position: "top",
        formatter: (params) => {
          const total = totals[params.dataIndex];
          return total > 0 ? total : "";
        },
        color: "#333",
      },
      data: Array(xAxisCategories.length).fill(0),
    });

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          let html = `<strong>${params[0].axisValue}</strong><br/>`;
          params.forEach((item) => {
            if (item.seriesName !== "Total" && item.value > 0) {
              html += `${item.marker} ${item.seriesName}: <b>${item.value}</b><br/>`;
            }
          });
          return html;
        },
      },
      legend: {
        data: modelArray,
        selected: legendState,
        bottom: 0,
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "10%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xAxisCategories,
      },
      yAxis: {
        type: "value",
      },
      series: series,
    };
  }, [filteredData, selectedYear, selectedQuarter, legendState, colorMap]);

  const onEvents = useMemo(
    () => ({
      click: (params) => {
        if (selectedYear === "all") {
          const clickedYear = parseInt(params.name);
          if (!isNaN(clickedYear)) {
            setSelectedYear(clickedYear);
          }
        } else if (selectedQuarter === "all") {
          setSelectedQuarter(params.name);
        }
      },
      legendselectchanged: (params) => {
        if (params && params.selected) {
          setLegendState(params.selected);
        }
      },
    }),
    [selectedYear, selectedQuarter, setSelectedYear, setSelectedQuarter]
  );

  return (
    <Card className="dashboard-panel">
      <Card.Header className="dashboard-panel-header d-flex justify-content-between align-items-center">
        <h6 className="mb-0">{t("dashboard.production_volume")}</h6>
        <Form.Select
          value={networkType}
          onChange={(e) => setNetworkType(e.target.value)}
          className="w-auto"
          size="sm"
        >
          <option value="All">{t("All")}</option>
          <option value="LW">LoRaWAN</option>
          <option value="NB">NB-IoT</option>
        </Form.Select>
      </Card.Header>
      <Card.Body>
        <ReactEcharts option={chartOption} style={style} notMerge onEvents={onEvents} />
      </Card.Body>
    </Card>
  );
}
